/**
 * FieldDetailsWPGraph
 * --------------------------------------------------------
 * Water-potential graph rendered inside the All-Farm View
 * "Field details" panel.
 *
 * Data-loading mirrors the field-view container
 * (WaterPotentialGraphContainer) almost line for line:
 *   - sensor data lives in Redux `selectedSensors`
 *   - `addSensorData` rebuilds the ordered set + pushes to the chart
 *   - `componentDidUpdate` watches selectedField id, history-just-loaded
 *     and temperature_h transitions and updates the overlay-only data
 *     via `updateDailyData` (no full chart rebuild on history arrival)
 *
 * Only the render is different — instead of a dropdown + checkbox list,
 * filters are presented as Figma-style chips: date range chips at the
 * top of the gray frame, sensor multi-select chips at the bottom.
 *
 * Chip selection — local state, not Redux
 * --------------------------------------------------------
 * The chip's "selected" appearance is driven by `state.selectedChipIds`
 * (and `state.loadingChipIds` for the in-flight spinner), NOT by
 * `props.selectedSensors`. This is deliberate so that:
 *
 *   1. Chip clicks paint instantly — the visual flip is a synchronous
 *      `setState` wrapped in `flushSync`, so it commits to the DOM
 *      before the Redux dispatch and the (heavy) Chart.js redraw run.
 *      No subscription-notification latency, no React-Redux scheduling
 *      surprises, no `transition` waiting on `background-color`.
 *   2. The chart redraw is deferred to a double `requestAnimationFrame`
 *      so the chip-flip paints in the current frame and Chart.js
 *      `loadData()` runs in the next.
 *
 * The trade-off: this component is the source of truth for chip state,
 * and `updateSelectionRedux` is the sink. If anything else in the app
 * dispatched `selected_entities` with new sensors during the component's
 * lifetime, the local state would silently desync. Today no other code
 * path mutates `selected_entities.sensors`, and on remount (field
 * change) the constructor + `componentDidUpdate`
 * field-change branch reseed local state from props — so the assumption
 * holds. If a future feature needs to change selected sensors from
 * outside this component, add an effect that syncs `selectedChipIds` /
 * `loadingChipIds` from `props.selectedSensors` on prop change.
 */
var React = require('react');
var { flushSync } = require('react-dom');
var { connect } = require('react-redux');
var { withTranslation } = require('react-i18next');
var sensorsManager = require('sensorsManager');
var weatherManager = require('weatherManager');
var distributionActions = require('distributionActions');
var GeneralClasses = require('GeneralClasses');
var dayjs = require('../../../utils/dayjsConfig');
var SensorGraph = require('SensorGraph');
var GraphToggle = require('GraphToggle');
var {
  wpSensorsNeedInitialLoad,
  selectedSensorsHaveLoadedData,
} = require('../wpSensorLoadHelpers');
var { cachedWeatherDataStatistics } = require('../weatherStatisticsCache');
var {
  DEFAULT_DAYS_BACK,
  MAX_DAYS_BACK,
  rangeOptionsFor,
  nextDaysBack,
} = require('./wpGraphRangeHelpers');
require('./FieldDetailsWPGraph.css');

class FieldDetailsWPGraph extends React.Component {
  constructor(props) {
    super(props);

    this.loadData = this.loadData.bind(this);
    this.loadDataForManySensors = this.loadDataForManySensors.bind(this);
    this.addSensorData = this.addSensorData.bind(this);
    this.removeSensorData = this.removeSensorData.bind(this);
    this.selectAllSensors = this.selectAllSensors.bind(this);
    this.onSensorSelection = this.onSensorSelection.bind(this);
    this.updateSelectionRedux = this.updateSelectionRedux.bind(this);
    this.clearData = this.clearData.bind(this);
    this.initGraph = this.initGraph.bind(this);
    this.onDaysBackChange = this.onDaysBackChange.bind(this);

    this.state = {
      days_back:
        props.field_state === 'archive' ? MAX_DAYS_BACK : DEFAULT_DAYS_BACK,
      timezone: '',
      units: '',
      // Local source of truth for chip selection. Driven by setState so
      // chip clicks paint instantly, independent of any react-redux
      // subscription latency or downstream chart-redraw work.
      selectedChipIds: Object.keys(props.selectedSensors || {}),
      // Chips currently waiting on a sensor_samples fetch. The chip
      // shows a spinner inside instead of the filled "selected" style
      // while its id is in here. Cleared by addSensorData when the
      // fetch resolves (or by the deselect path if the user changes
      // their mind mid-flight).
      loadingChipIds: [],
    };
  }

  componentDidMount() {
    if (this.props.selectedField) {
      const daysBack = this.defaultDaysBack();
      this.setState({ days_back: daysBack });
      this.initGraph(this.props.selectedField, daysBack);
    }
  }

  // Archive fields default to the full history ("Max"); active fields keep the
  // standard default. Mirrors WaterPotentialGraphContainer's archive behavior.
  defaultDaysBack() {
    return this.props.field_state === 'archive'
      ? MAX_DAYS_BACK
      : DEFAULT_DAYS_BACK;
  }

  componentDidUpdate(prevProps) {
    const fieldChanged =
      this.props.selectedField?.id !== prevProps.selectedField?.id;
    const fieldStateChanged = this.props.field_state !== prevProps.field_state;

    const historyJustLoaded =
      this.props.loadHistoryStatus?.status === 'success' &&
      prevProps.loadHistoryStatus?.status !== 'success';

    const temperatureChanged =
      this.props.temperature_h !== prevProps.temperature_h;

    if (fieldChanged || fieldStateChanged) {
      // Field (or archive/active mode) switched — reset the chip selection;
      // selectAllSensors (via initGraph) repopulates it. Archive fields
      // default to the full history ("Max"); leaving archive resets to the
      // default range; active → active keeps the current range.
      const daysBack = nextDaysBack({
        prevFieldState: prevProps.field_state,
        fieldState: this.props.field_state,
        currentDaysBack: this.state.days_back,
      });
      this.setState({
        selectedChipIds: [],
        loadingChipIds: [],
        days_back: daysBack,
      });
      this.initGraph(this.props.selectedField, daysBack);
      return;
    }

    // Self-heal: on app load (and farming-unit switch) the field is
    // auto-selected and its sensors are (re)built with state INIT, but the
    // graph only (re)loads sensor lines when selectedField.id changes. The
    // initial initGraph for the auto-selected field can run before the field
    // object is ready, and the field-history refresh then replaces the field
    // with a fresh INIT sensor set at the *same id* — so the field-change
    // branch above never re-fires and the WP lines are never fetched (the bug
    // where sensors only appear on the first explicit field load per farming
    // unit). Detect that stuck state and kick off the load once, without
    // needing an explicit re-selection. Pass selectedSensors so a successful
    // first load is not cleared when history refresh swaps selectedField for
    // INIT copies (otherwise ~1s flicker on farming-unit switch). See
    // wpSensorLoadHelpers.js for the loop-safety guards.
    if (
      wpSensorsNeedInitialLoad(this.props.selectedField, {
        loadingChipIds: this.state.loadingChipIds,
        selectedSensors: this.props.selectedSensors,
      })
    ) {
      const daysBack = nextDaysBack({
        prevFieldState: prevProps.field_state,
        fieldState: this.props.field_state,
        currentDaysBack: this.state.days_back,
      });
      this.initGraph(this.props.selectedField, daysBack);
      return;
    }

    // Daily overlays (temp / ETc / irrigation / rain) arrived after the
    // sensor lines. Update only the overlays so we don't kick off a
    // full sensor reload — same approach as Field View.
    if (historyJustLoaded || temperatureChanged) {
      if (
        this.sensor_graph &&
        this.props.selectedSensors &&
        this.props.selectedField &&
        Object.keys(this.props.selectedSensors).length > 0
      ) {
        const tz = this.props.selectedField.geo_data?.time_zone;
        const temperatureStatistics = cachedWeatherDataStatistics(
          weatherManager.weatherDataStatistics,
          this.props.temperature_h?.values,
          tz,
        );
        const etcStatistics = cachedWeatherDataStatistics(
          weatherManager.weatherDataStatistics,
          this.props.selectedField.historical_data?.baseline_etc_by_kc_table
            ?.values,
          tz,
        );

        if (this.sensor_graph.updateDailyData) {
          this.sensor_graph.updateDailyData(
            this.props.selectedField.historical_data,
            tz,
            temperatureStatistics,
            etcStatistics,
          );
        } else {
          const soilData =
            this.props.soil_data?.soil_data?.[
              this.props.selectedField.soil_type?.toLowerCase()
            ];
          this.sensor_graph.loadData(
            this.props.selectedSensors,
            this.props.selectedField.historical_data,
            parseInt(soilData?.suggestedMinWPValue),
            tz,
            temperatureStatistics,
            etcStatistics,
          );
        }
      }
    }
  }

  // --- data-flow methods (mirror WaterPotentialGraphContainer) ---

  // Same keep-stale approach as Adrian/perf/main_thread_stuck: leave the
  // previous field's chart on screen until the new field's samples land
  // (addSensorData → loadData replaces the series). Only wipe when we know
  // there is nothing to draw for the current field.
  clearStaleChartIfNoData() {
    if (this.state.loadingChipIds.length > 0) return;
    if (selectedSensorsHaveLoadedData(this.props.selectedSensors)) return;
    this.clearData();
  }

  initGraph(field, days_back) {
    if (!field || !field.sensors || Object.keys(field.sensors).length === 0) {
      this.clearData();
      return;
    }
    // No eager clearData() — preserve the on-screen chart through the
    // field switch; the first addSensorData replaces it with the new field.
    const tz = field.geo_data?.time_zone;
    this.setState({ timezone: tz, days_back });

    const currentSelected = this.props.selectedSensors || {};
    // Only reuse Redux selection ids that exist as WP sensors on *this*
    // field — stale ids from the previous field would sit in loadingChipIds
    // forever (loadData early-returns) and never paint the replacement.
    const validIds = Object.keys(currentSelected).filter(
      (id) => field.sensors?.[id]?.type === 'wp',
    );
    if (validIds.length > 0) {
      const next = {};
      validIds.forEach((id) => {
        next[id] = field.sensors[id];
      });
      this.setState({
        selectedChipIds: validIds,
        loadingChipIds: validIds.slice(),
      });
      this.updateSelectionRedux(next);
      this.loadDataForManySensors(undefined, validIds, tz, days_back);
    } else {
      this.selectAllSensors(days_back);
    }
  }

  selectAllSensors(days_back) {
    if (!this.props.selectedField?.sensors) return;
    const wpSensorIds = Object.keys(this.props.selectedField.sensors).filter(
      (id) => this.props.selectedField.sensors[id]?.type === 'wp',
    );
    if (wpSensorIds.length === 0) {
      this.clearData();
      this.setState({ selectedChipIds: [], loadingChipIds: [] });
      this.updateSelectionRedux({});
      return;
    }
    const next = {};
    wpSensorIds.forEach((id) => {
      next[id] = this.props.selectedField.sensors[id];
    });
    // Mark every WP chip as both selected (intent) and loading
    // (waiting for its fetch). addSensorData clears the loading flag
    // per sensor as each fetch resolves.
    this.setState({
      selectedChipIds: wpSensorIds,
      loadingChipIds: wpSensorIds.slice(),
    });
    this.updateSelectionRedux(next);
    this.loadDataForManySensors(
      null,
      wpSensorIds,
      this.props.selectedField.geo_data?.time_zone,
      days_back,
    );
  }

  loadDataForManySensors(
    event,
    sensors,
    timezone,
    days_back = DEFAULT_DAYS_BACK,
  ) {
    if (event) {
      event.preventDefault();
    }
    this.setState({
      days_back: days_back,
      timezone: timezone || this.state.timezone,
    });
    sensors.forEach((sensorId) => this.loadData(sensorId, timezone, days_back));
  }

  loadData(sensor_id, timezone, days_back = DEFAULT_DAYS_BACK) {
    if (!sensor_id || !timezone) return;
    if (
      !this.props.selectedField?.sensors ||
      !(sensor_id.trim() in this.props.selectedField.sensors)
    ) {
      return;
    }

    const that = this;
    const fieldIdAtRequest = this.props.selectedField.id;
    this.props.selectedField.sensors[sensor_id.trim()].state =
      GeneralClasses.SENSOR_STATES.LOADING;

    sensorsManager
      .sensor_samples(this.props.selectedField.id, sensor_id, days_back)
      .then(
        function (res) {
          if (
            !that.sensor_graph ||
            !that.props.selectedField ||
            that.props.selectedField.id !== fieldIdAtRequest
          ) {
            return;
          }
          if ('code' in res && res.code === 1001) {
            if (sensor_id in that.props.selectedField.sensors) {
              that.props.selectedField.sensors[sensor_id].state =
                GeneralClasses.SENSOR_STATES.COMPLETE;
              that.setState(
                (prev) => ({
                  loadingChipIds: prev.loadingChipIds.filter(
                    (id) => id !== sensor_id,
                  ),
                }),
                () => that.clearStaleChartIfNoData(),
              );
            }
            return;
          }
          if (
            res.sensor_data &&
            res.sensor_data.length > 0 &&
            res.serial_number in that.props.selectedField.sensors
          ) {
            const data = res.sensor_data.map((sample, index) => ({
              value: parseFloat(sample.value),
              date: dayjs.unix(sample.date),
              index,
            }));

            // Sensor color — derived from a stable sorted index of WP sensors
            let wpSensorIds = Object.keys(
              sensorsManager.getWPSensors(that.props.selectedField.sensors),
            ).sort();
            let sensorIndex = wpSensorIds.indexOf(res.serial_number);
            if (sensorIndex === -1) {
              wpSensorIds = Object.keys(that.props.selectedField.sensors)
                .filter(
                  (id) => that.props.selectedField.sensors[id].type === 'wp',
                )
                .sort();
              sensorIndex = wpSensorIds.indexOf(res.serial_number);
            }
            const color = sensorsManager.sensorColorByIndex(
              sensorIndex,
              that.props.field_state,
            );
            const sensor = that.props.selectedField.sensors[res.serial_number];
            sensor.color = color;
            sensor.data = data;
            sensor.state = GeneralClasses.SENSOR_STATES.COMPLETE;
            sensor.cachedDaysBack = days_back;
            that.setState({ units: res.units });
            that.addSensorData(sensor);
          } else {
            that.removeSensorData(sensor_id);
          }
        },
        function (error) {
          console.error('Error loading sensor data:', error);
          if (
            that.props.selectedField &&
            sensor_id in that.props.selectedField.sensors
          ) {
            that.props.selectedField.sensors[sensor_id].state =
              GeneralClasses.SENSOR_STATES.COMPLETE;
            that.setState(
              (prev) => ({
                loadingChipIds: prev.loadingChipIds.filter(
                  (id) => id !== sensor_id,
                ),
              }),
              () => that.clearStaleChartIfNoData(),
            );
          }
        },
      );
  }

  addSensorData(newSensorData) {
    if (!this.props.selectedSensors || !this.props.selectedField) return;
    const currentSelected = this.props.selectedSensors;
    const soilData =
      this.props.soil_data?.soil_data?.[
        this.props.selectedField.soil_type?.toLowerCase()
      ];

    currentSelected[newSensorData.id] = newSensorData;
    newSensorData.displayName =
      sensorsManager.sensorShortDisplayName(newSensorData);

    const ordered = {};
    Object.keys(this.props.selectedField.sensors)
      .sort()
      .forEach((sensorId) => {
        if (currentSelected[sensorId]) {
          ordered[sensorId] = currentSelected[sensorId];
        }
      });

    const tz = this.props.selectedField.geo_data?.time_zone;
    // Cached: this runs once per WP sensor response with the same series.
    const temperatureStatistics = cachedWeatherDataStatistics(
      weatherManager.weatherDataStatistics,
      this.props.temperature_h?.values,
      tz,
    );
    const etcStatistics = cachedWeatherDataStatistics(
      weatherManager.weatherDataStatistics,
      this.props.selectedField.historical_data?.baseline_etc_by_kc_table
        ?.values,
      tz,
    );

    this.sensor_graph?.loadData(
      ordered,
      this.props.selectedField.historical_data,
      parseInt(soilData?.suggestedMinWPValue),
      tz,
      temperatureStatistics,
      etcStatistics,
    );

    this.setState((prev) => {
      const inSelected = prev.selectedChipIds.includes(newSensorData.id);
      const inLoading = prev.loadingChipIds.includes(newSensorData.id);
      if (!inLoading && inSelected) return null;
      return {
        selectedChipIds: inSelected
          ? prev.selectedChipIds
          : [...prev.selectedChipIds, newSensorData.id],
        loadingChipIds: inLoading
          ? prev.loadingChipIds.filter((id) => id !== newSensorData.id)
          : prev.loadingChipIds,
      };
    });
    this.updateSelectionRedux(ordered);
  }

  removeSensorData(sensorId) {
    if (
      !this.props.selectedField?.sensors ||
      !(sensorId in this.props.selectedField.sensors)
    ) {
      return;
    }
    const currentSelected = { ...(this.props.selectedSensors || {}) };
    // Keep the sensor's `state` at COMPLETE (and preserve `data` /
    // `cachedDaysBack`) on the sensor object so re-selecting the chip
    // hits the cache fast-path in onSensorSelection instead of
    // re-fetching from the server.
    delete currentSelected[sensorId];
    this.sensor_graph?.removeSensor(sensorId);
    this.setState(
      (prev) => ({
        selectedChipIds: prev.selectedChipIds.filter((id) => id !== sensorId),
        loadingChipIds: prev.loadingChipIds.filter((id) => id !== sensorId),
      }),
      () => this.clearStaleChartIfNoData(),
    );
    this.updateSelectionRedux(currentSelected);
  }

  onSensorSelection(sensor) {
    const currentlySelected = this.state.selectedChipIds.includes(sensor.id);
    if (!currentlySelected) {
      const currentSensor = this.props.selectedField.sensors[sensor.id];
      const canUseCache =
        currentSensor.data &&
        currentSensor.data.length > 0 &&
        currentSensor.state === GeneralClasses.SENSOR_STATES.COMPLETE &&
        currentSensor.cachedDaysBack === this.state.days_back;

      // Cache hit: flip the chip to "selected" immediately; data is
      // already on the sensor object so the curve appears next frame
      // with no fetch delay.
      // Cache miss: add to loadingChipIds and to selectedChipIds — the
      // chip renders a spinner (outline only) until addSensorData
      // clears the loading state when the fetch resolves.
      // flushSync forces React to commit this state change to the DOM
      // synchronously before we return from the event handler — so the
      // chip's new style hits the next paint regardless of how the rAF
      // / Redux dispatch / chart-redraw work below is scheduled.
      flushSync(() => {
        this.setState((prev) => ({
          selectedChipIds: prev.selectedChipIds.includes(sensor.id)
            ? prev.selectedChipIds
            : [...prev.selectedChipIds, sensor.id],
          loadingChipIds: canUseCache
            ? prev.loadingChipIds
            : prev.loadingChipIds.includes(sensor.id)
              ? prev.loadingChipIds
              : [...prev.loadingChipIds, sensor.id],
        }));
      });

      // Dispatch Redux now so that addSensorData (running later in
      // rAF) rebuilds `ordered` with the new sensor + every
      // previously-selected one. Without this, a concurrent fetch
      // resolution for another sensor can perturb selectedSensors
      // between the click and the rAF, dropping unrelated curves.
      const currentSelected = { ...(this.props.selectedSensors || {}) };
      currentSelected[currentSensor.id] = currentSensor;
      this.updateSelectionRedux(currentSelected);

      // Defer the heavy chart work to the next frame so the
      // chip-flip / spinner paints first.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (canUseCache) {
            this.addSensorData(currentSensor);
          } else {
            this.loadData(sensor.id, this.state.timezone, this.state.days_back);
          }
        });
      });
    } else {
      // Deselect — flip the chip immediately via local state. Also
      // clear any in-flight loading state so a mid-flight deselect
      // doesn't leave the spinner spinning if the fetch resolves
      // after the user changes their mind. flushSync forces React to
      // commit the chip-flip DOM update before the chart removal
      // (deferred below) runs.
      flushSync(() => {
        this.setState((prev) => ({
          selectedChipIds: prev.selectedChipIds.filter(
            (id) => id !== sensor.id,
          ),
          loadingChipIds: prev.loadingChipIds.filter((id) => id !== sensor.id),
        }));
      });

      const currentSelected = { ...(this.props.selectedSensors || {}) };
      delete currentSelected[sensor.id];
      this.updateSelectionRedux(currentSelected);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.sensor_graph?.removeSensor(sensor.id);
        });
      });
    }
  }

  updateSelectionRedux(selectedSensors) {
    // The SET_SELECTED_ENTITIES reducer replaces all four fields with the
    // payload (rather than merging), so we must pass the current
    // distribution / farming_unit / field through — otherwise updating
    // sensors here would null out the field and unmount this component.
    this.props.setSelectedEntities(
      this.props.selectedDistribution,
      this.props.selectedFarmingUnit,
      this.props.selectedField,
      selectedSensors,
    );
  }

  clearData() {
    if (this.sensor_graph) {
      this.sensor_graph.loadData(null, null, undefined, undefined);
    }
  }

  // Switch the visible date range (chip click or the small-width dropdown).
  onDaysBackChange(days) {
    const { selectedField } = this.props;
    if (days === this.state.days_back || !selectedField) return;
    // Reload the locally-selected sensors for the new range.
    // selectedChipIds is this component's source of truth for
    // selection (Redux selectedSensors can be empty/stale), so
    // relying on Redux here made the chip a no-op. Fall back to
    // Redux, then to selecting all WP sensors.
    const ids =
      this.state.selectedChipIds.length > 0
        ? this.state.selectedChipIds
        : Object.keys(this.props.selectedSensors || {});
    if (ids.length === 0) {
      this.selectAllSensors(days);
      return;
    }
    this.setState({ loadingChipIds: ids.slice() });
    this.loadDataForManySensors(
      null,
      ids,
      selectedField.geo_data?.time_zone,
      days,
    );
  }

  // --- render ---

  render() {
    const { selectedField, t } = this.props;
    if (!selectedField) {
      return (
        <div className='field-details-wp__empty'>
          {t('select_a_field_to_view_details')}
        </div>
      );
    }

    // Show WP-only chips. Piezometer (and other non-WP) sensors are
    // not water-potential signals and shouldn't appear in this legend.
    // Filter by `type` string rather than `instanceof WPSensor` —
    // the selected_entities Redux action rebuilds sensors via
    // `{...currentSensor, state: INIT}` on field change, which strips
    // the class instance. sensorsManager.getWPSensors() uses
    // instanceof and would return {} after that rebuild, hiding the
    // chip legend until something later restored the types.
    const wpSensors = Object.entries(selectedField.sensors || {}).reduce(
      (acc, [id, s]) => {
        if (s?.type === 'wp') {
          acc[id] = s;
        }
        return acc;
      },
      {},
    );
    const wpSensorIds = Object.keys(wpSensors).sort();
    // Chip-selected state is driven by LOCAL state, not Redux, so the
    // visual flip on click is instant and not gated on the chart's
    // (heavy) redraw or any react-redux subscription scheduling.
    const selectedSensorIds = this.state.selectedChipIds;
    const sensorsLoading = Object.values(wpSensors).some(
      (s) => s.state === GeneralClasses.SENSOR_STATES.LOADING,
    );

    // GraphToggle (drop / accumulated / temperature buttons) is only
    // rendered when the parent passes `onToggleClick` — Field View
    // (DashboardContent) and the Farm View Field details tab supply it.
    const showGraphToggle = typeof this.props.onToggleClick === 'function';
    // Field details tab: "Graphs" title left, icon tabs centered, range
    // selection right (pills wide, dropdown when the panel is narrow).
    const graphsLayout = Boolean(this.props.graphsLayout);
    // The "Max" option is only offered in the Graphs layout.
    const rangeOptions = rangeOptionsFor(graphsLayout);
    const selectedRange = rangeOptions.find(
      (opt) => opt.days === this.state.days_back,
    );
    const selectedRangeLabel = selectedRange
      ? t(selectedRange.rangeLabelKey)
      : '';

    return (
      <div
        className={`field-details-wp ${
          this.props.fluidHeight ? 'field-details-wp--fluid' : ''
        } ${graphsLayout ? 'field-details-wp--graphs' : ''}`}
      >
        {/* Header (outside the gray frame): toggle + title left, date chips right */}
        <div className='field-details-wp__header'>
          <div className='field-details-wp__title-group'>
            {showGraphToggle && (
              <div className='field-details-wp__toggle'>
                <GraphToggle
                  initialValue='drop'
                  onToggleClick={this.props.onToggleClick}
                  icon={this.props.toggleIcons}
                  temperatureUnit={this.props.temperatureUnit}
                />
              </div>
            )}
            <span className='field-details-wp__title'>
              {graphsLayout ? t('graphs') : t('water_potential')}
            </span>
          </div>
          {/* Archive mode always shows the full history (Max) and hides the
              date-range chips entirely. Active fields keep the chip row. */}
          {this.props.field_state !== 'archive' && (
            <div className='field-details-wp__date-chips' role='tablist'>
              {rangeOptions.map((opt) => (
                <button
                  key={opt.days}
                  type='button'
                  role='tab'
                  aria-selected={opt.days === this.state.days_back}
                  className={`field-details-wp__date-chip ${
                    opt.days === this.state.days_back
                      ? 'field-details-wp__date-chip--selected'
                      : ''
                  }`}
                  onClick={() => this.onDaysBackChange(opt.days)}
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>
          )}
          {/* Narrow tab: the chips collapse into this dropdown (CSS swaps
              them) — same Bootstrap dropdown as the Soil temperature graph. */}
          {graphsLayout && this.props.field_state !== 'archive' && (
            <div className='dropdown field-details-wp__range-dropdown text-size-16 text-grey71'>
              <div
                className='dropdown-toggle'
                data-toggle='dropdown'
                aria-haspopup='true'
                aria-expanded='false'
                style={{ cursor: 'pointer' }}
              >
                {selectedRangeLabel}
              </div>
              <div className='dropdown-menu dropdown-menu-right'>
                {rangeOptions.map((opt) => (
                  <a
                    key={opt.days}
                    className='dropdown-item text-grey71'
                    href='#'
                    onClick={(e) => {
                      e.preventDefault();
                      this.onDaysBackChange(opt.days);
                    }}
                  >
                    {t(opt.rangeLabelKey)}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Gray frame wraps chart + sensor legend */}
        <div className='field-details-wp__frame'>
          {graphsLayout && (
            <div className='field-details-wp__chart-title'>
              {t('water_potential')}
            </div>
          )}
          <div className='field-details-wp__chart'>
            <SensorGraph
              onRef={(ref) => (this.sensor_graph = ref)}
              units={this.state.units}
              isLoading={sensorsLoading}
              selectedField={selectedField}
              selectedFarmingUnit={this.props.selectedFarmingUnit}
              selectedDistribution={this.props.selectedDistribution}
              shouldDrawInputs={true}
              shouldDrawBottom={true}
              shouldUseNewEventDesign={true}
              shouldDrawTodayMark={false}
              shouldDrawResetButton={true}
              toolTipMode={'nearest'}
              distributionMode={'linear'}
              shouldDisplayZoom={true}
              shouldSpaceData={true}
              shouldDiplayUTC={false}
              shouldScaleGraph={false}
              field_state={this.props.field_state}
            />
          </div>
          {wpSensorIds.length > 0 && (
            <div className='field-details-wp__legend'>
              <span className='field-details-wp__legend-label'>
                {t('select_sensors')}
              </span>
              <div className='field-details-wp__sensor-chips'>
                {wpSensorIds.map((id, i) => {
                  const s = wpSensors[id];
                  const inSelected = selectedSensorIds.includes(id);
                  const isLoading = this.state.loadingChipIds.includes(id);
                  // While the fetch is in flight the chip stays
                  // outline-only with a spinner. Only flip to the
                  // filled "selected" style once data has landed.
                  const selected = inSelected && !isLoading;
                  const color = sensorsManager.sensorColorByIndex(
                    i,
                    this.props.field_state,
                  );
                  const transmitterId = s.transmitter_id || s.id;
                  const label = s.name
                    ? `${s.name} - ${transmitterId}`
                    : transmitterId;
                  const style = selected
                    ? {
                        backgroundColor: color,
                        borderColor: color,
                        color: '#fff',
                      }
                    : { borderColor: color, color: color };
                  return (
                    <button
                      key={id}
                      type='button'
                      className={`field-details-wp__sensor-chip ${
                        selected
                          ? 'field-details-wp__sensor-chip--selected'
                          : ''
                      }`}
                      style={style}
                      onClick={() => this.onSensorSelection(s)}
                      title={label}
                    >
                      {label}
                      {isLoading && (
                        <span
                          className='field-details-wp__sensor-chip-spinner'
                          role='status'
                          aria-hidden='true'
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const field = state.distribution_data.selected_entities.field;
  return {
    selectedField: field,
    selectedSensors: state.distribution_data.selected_entities.sensors,
    selectedFarmingUnit: state.distribution_data.selected_entities.farming_unit,
    selectedDistribution:
      state.distribution_data.selected_entities.distribution,
    temperature_h:
      field && field.historical_data && field.historical_data.temperature_h,
    soil_data: state.soil_data?.soil_data,
    loadHistoryStatus: state.distribution_data.field_load_history,
    field_state: state.distribution_data.field_state,
  };
};

const mapDispatchToProps = {
  setSelectedEntities: distributionActions.selected_entities,
};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(FieldDetailsWPGraph));
